/**
 * #2058 — IntegratedSoulInterview
 *
 * The INTEGRATED, continuous, full-bleed soul-intake surface (Sean, 2026-06-24:
 * "go more full screen or incorporate into the actual chat … the interview should
 * feel integrated"). It replaces the boxed `AdaptiveSoulIntakePanel` modal when
 * `CRUCIBLE_INTAKE=1` (crucible#2682 collapsed the old standalone
 * `CRUCIBLE_INTERVIEW_INTEGRATED` var into this one primary gate) — same
 * proven `intake:turn`/`intake:answer` IPC drive loop. The standard Prosumer
 * flow uses the desktop surface; compact phone/TV surfaces remain available
 * to legacy and internal callers.
 *
 * Each turn is rendered by the chat-native `SoulInterviewTurnCard`. During the
 * ~6-10s inter-turn LLM pause a "Shaping the next question… Ns" thinking row appears
 * so the pause never reads as frozen.
 *
 * Multimodal (v2): the component subscribes to the `intake:visual` push channel.
 * When a visual arrives it renders a `SoulInterviewVisualCard` (shimmer → revealed)
 * above the corresponding turn card. Images are decoupled from the turn wire — the
 * turn text is served immediately and the image arrives seconds later.
 *
 * Lifecycle mirrors `AdaptiveSoulIntakePanel`: mount → `intake:turn` (turn 0),
 * each answer → `intake:answer` (the next turn or `done`), `done` → `onDone()`.
 * The component never calls IPC for the answer directly through the card — it owns
 * the invoke so the card stays a pure presentational unit.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  SoulIntakeCommandSource,
  SoulIntakePlanPreparationPhase,
  SoulIntakeTurnView,
  SoulIntakeTurnVisual,
  TurnVisualMedia,
} from '@volley/crucible-ipc-types';
import {
  isDecideForMeCommand,
  SoulInterviewTurnCard,
  type SoulInterviewAnswerInput,
} from './SoulInterviewTurnCard';
import { SoulInterviewVisualCard } from './SoulInterviewVisualCard';
import {
  GuidedInterviewSemanticComposition,
  type GuidedInterviewSemanticSlotStates,
} from './GuidedInterviewSemanticComposition';
import { useCrucibleClient } from '../transport/context.js';
import { isNoRecoverableIntakeCheckpoint } from '../workspace/interview-recovery';

const MIN_VISIBLE_THINKING_MS = 250;

// 2026-07-10 — a turn's visual can be seeded `undefined` (shimmer) purely
// because the producer's `expectVisual` flag says the image hooks are
// WIRED (adaptive-session.ts's `imagesWired`), independent of whether
// generation actually succeeds. With no FAL key in the environment,
// `defaultTurnImageGenerator` returns null immediately and
// `tryPushTurnVisual` swallows the failure silently — the wire shape's
// `image` field is required, so there's no way to push an explicit
// "gave up" marker today, and no push means no timeout either. Sean,
// live-testing: "it's not generating art right now, so that bar is
// confusing." This is a client-side backstop, not the durable fix (the
// durable fix is upstream — give the wire a way to say "tried and
// failed"); filed as a follow-up. Generous enough not to cut off a real
// in-flight generation, short enough that the shimmer can't hang forever.
//
// 2026-07-10 — measured the moodboard turn's 3 PARALLEL tile generations
// against the real asset-forge/FAL pipeline: 19-24s just for the image
// calls themselves (a single-visual REVEAL-turn generation is faster, one
// image not three). The old 25s budget left almost no headroom against
// ordinary network/queue variance, so a real in-flight batch would
// routinely lose the race and blank out tiles that were about to land.
const VISUAL_GENERATION_TIMEOUT_MS = 45_000;

const PLAN_PREPARATION_LABELS: Record<SoulIntakePlanPreparationPhase, string> = {
  'setting-up-project': 'Setting up your project',
  'generating-design': 'Generating the design direction',
  'generating-story': 'Generating the story foundation',
  'saving-plan': 'Saving the editable build plan',
  'finalizing-project': 'Finalizing project files',
  'opening-review': 'Opening plan review',
};

export interface IntegratedSoulInterviewProps {
  /** kebab-case game id under design (for context / headers). */
  gameId: string;
  /** Fires once the terminal handoff is ready for the owning workspace. */
  onDone?: () => void;
  /** Fires when the owning workspace mounted from stale state but intake has no live or durable session. */
  onUnavailable?: () => void;
  /**
   * Phone mode keeps the controller as input only. Rich context is emitted via
   * onSnapshot for the TV surface to render. `'tv'` mounts the same turn card
   * inside the TV preview pane itself (crucible#2610 Phase 3 consumer-preview
   * mount) with 10-foot typography and no free-text input.
   */
  surface?: 'chat' | 'phone' | 'tv' | 'desktop';
  /** Live read model for the paired TV surface. */
  onSnapshot?: (snapshot: IntegratedSoulInterviewSnapshot) => void;
}

export interface IntegratedSoulInterviewSnapshot {
  gameId: string;
  current: SoulIntakeTurnView | null;
  historyCount: number;
  thinking: boolean;
  thinkingElapsedSec: number;
  finalizing: boolean;
  finalizingElapsedSec: number;
  error: string | null;
  visual: SoulIntakeTurnVisual | null | undefined;
  selectedOption: { label: string; flavor?: string } | null;
  freeText: string;
  /**
   * crucible#2912 Bugbot round — the phone answers a question via
   * `intake:command` free text/voice only (no `optionIndex`), so
   * `selectedOption` is legitimately null for those turns. Without this, the
   * TV "Current pick" panel showed "Pick one on your phone" during the
   * inter-turn thinking window right after a free-text/voice answer, even
   * though a real answer had just landed. Populated only for the settled
   * turn's free-text answer (not the live draft — that's `freeText` above).
   */
  lastAnswerFreeText: string | null;
  /**
   * crucible#2661 Bugbot fix (bc443317) — the moodboard turn's candidate board
   * images, keyed by boardIndex. The phone's own card reads this straight from
   * local state (`boardVisuals` below); the TV mount has no equivalent local
   * accumulation (it's a separate presentational render, not a second
   * IntegratedSoulInterview instance), so it must read it off the snapshot.
   * Without this, the TV's moodboard tiles show the generating shimmer forever,
   * even after the phone's images have loaded.
   */
  boardVisuals: ReadonlyMap<number, TurnVisualMedia | null | undefined>;
  /** 2026-07-14 — true while a moodboard steering regenerate is in flight
   *  (phone-initiated; the TV mirrors this so its display-only card can show
   *  the same "steering…" state instead of looking frozen). */
  steering: boolean;
}

/** One settled exchange kept on screen as the transcript grows (the continuous feel). */
interface SettledTurn {
  turn: SoulIntakeTurnView;
  answer: SoulInterviewAnswerInput;
}

export function IntegratedSoulInterview({
  gameId,
  onDone,
  onUnavailable,
  surface = 'chat',
  onSnapshot,
}: IntegratedSoulInterviewProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [history, setHistory] = useState<SettledTurn[]>([]);
  const [current, setCurrent] = useState<SoulIntakeTurnView | null>(null);
  const [thinking, setThinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [thinkingElapsedSec, setThinkingElapsedSec] = useState(0);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizingElapsedSec, setFinalizingElapsedSec] = useState(0);
  const [planPreparationPhase, setPlanPreparationPhase] =
    useState<SoulIntakePlanPreparationPhase | null>(null);
  const [planPreparationStartedAt, setPlanPreparationStartedAt] = useState<number | null>(null);
  const [skipTarget, setSkipTarget] = useState<'build' | 'design' | null>(null);
  const [planReviewFailed, setPlanReviewFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftAnswer, setDraftAnswer] = useState<SoulInterviewAnswerInput>({});
  const onDoneRef = useRef(onDone);
  const onUnavailableRef = useRef(onUnavailable);
  const answerInFlightRef = useRef(false);
  const skipInFlightRef = useRef(false);
  const answerIdsRef = useRef(new Map<number, string>());
  // The answer-submit path resolves an async IPC and then setStates. Guard it so
  // a component unmount mid-flight (e.g. the create flow navigates away) doesn't
  // setState on an unmounted component. The mount effect has its own `cancelled`
  // flag; this covers the submit useCallback, which has no such scope.
  const mountedRef = useRef(true);
  // Visual map: questionIndex (0-based) → visual. A PRESENT key whose value is
  // `undefined` = shimmer (image generating); `null` = hide (no image for this
  // turn); a value = the resolved image. An ABSENT key falls through to `null`
  // (hide) at the read site. The live turn is seeded `undefined` (shimmer).
  const [visuals, setVisuals] = useState<Map<number, SoulIntakeTurnVisual | null | undefined>>(
    new Map(),
  );
  // #2058 P2 — moodboard candidate tile images keyed by boardIndex (0..2). An
  // absent/`undefined` key = shimmer (generating); a value = the board's image.
  // Reset per turn (only the moodboard turn populates it).
  const [boardVisuals, setBoardVisuals] = useState<Map<number, TurnVisualMedia | null | undefined>>(
    new Map(),
  );
  const [boardVisualAttempts, setBoardVisualAttempts] = useState<
    Map<number, SoulIntakeTurnVisual['attempt']>
  >(new Map());
  const visualAttemptNumbersRef = useRef(new Map<string, number>());
  const [visualRetryEpoch, setVisualRetryEpoch] = useState(0);
  const planReviewPollEpochRef = useRef(0);

  const acceptVisualAttempt = useCallback((visual: SoulIntakeTurnVisual): boolean => {
    const attemptKey = `${visual.questionIndex}:${visual.boardIndex ?? 'single'}`;
    const attemptNumber = visual.attempt?.attemptNumber;
    const latestAttemptNumber = visualAttemptNumbersRef.current.get(attemptKey) ?? 0;
    if (attemptNumber !== undefined && attemptNumber < latestAttemptNumber) return false;
    if (attemptNumber !== undefined) {
      visualAttemptNumbersRef.current.set(attemptKey, attemptNumber);
    }
    return true;
  }, []);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      planReviewPollEpochRef.current += 1;
    };
  }, []);

  useEffect(() => {
    // Cancel an old project's status request even though this component stays
    // mounted while its gameId changes.
    planReviewPollEpochRef.current += 1;
  }, [client, gameId]);

  // Subscribe to `intake:visual` push channel for async turn images.
  // A push WITHOUT boardIndex = the single REVEAL image (keyed by questionIndex).
  // A push WITH boardIndex = one moodboard candidate tile (keyed by boardIndex);
  // the 3 tiles share a questionIndex but stream in independently.
  useEffect(() => {
    const unsub = client.subscribe('intake:visual', (visual) => {
      if (!acceptVisualAttempt(visual)) return;
      if (typeof visual.boardIndex === 'number') {
        const boardIndex = visual.boardIndex;
        setBoardVisuals((prev) => new Map(prev).set(boardIndex, visual.image));
        setBoardVisualAttempts((prev) => new Map(prev).set(boardIndex, visual.attempt));
        return;
      }
      setVisuals((prev) => new Map(prev).set(visual.questionIndex, visual));
    });
    return unsub;
  }, [acceptVisualAttempt, client]);

  const awaitPlanReview = useCallback(
    async (target: 'build' | 'design' | null): Promise<void> => {
      const pollEpoch = ++planReviewPollEpochRef.current;
      const isCurrentPoll = (): boolean =>
        mountedRef.current && planReviewPollEpochRef.current === pollEpoch;
      if (target !== 'build') {
        if (isCurrentPoll()) onDoneRef.current?.();
        return;
      }
      setPlanReviewFailed(false);
      while (isCurrentPoll()) {
        try {
          const status = await client.invoke('intake:plan-review-status', { gameId });
          if (!isCurrentPoll()) return;
          if (status.state === 'ready' || status.state === 'not-applicable') {
            onDoneRef.current?.();
            return;
          }
          if (status.state === 'preparing') {
            setPlanPreparationPhase(status.phase ?? null);
            const parsed = status.phaseStartedAt ? Date.parse(status.phaseStartedAt) : NaN;
            setPlanPreparationStartedAt(Number.isFinite(parsed) ? parsed : null);
          }
          if (status.state === 'failed') {
            setFinalizing(false);
            setPlanReviewFailed(true);
            setError('We couldn’t prepare the build plan. Your interview answers are saved.');
            return;
          }
        } catch {
          if (isCurrentPoll()) {
            setFinalizing(false);
            setPlanReviewFailed(true);
            setError('We couldn’t prepare the build plan. Your interview answers are saved.');
          }
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 750));
      }
    },
    [client, gameId],
  );

  // Load turn 0 on mount (the REVEAL + first question).
  useEffect(() => {
    let cancelled = false;
    answerInFlightRef.current = false;
    skipInFlightRef.current = false;
    setHistory([]);
    setCurrent(null);
    setThinking(false);
    setSaving(false);
    setThinkingElapsedSec(0);
    setFinalizing(false);
    setFinalizingElapsedSec(0);
    setPlanPreparationPhase(null);
    setPlanPreparationStartedAt(null);
    setSkipTarget(null);
    setPlanReviewFailed(false);
    setError(null);
    setDraftAnswer({});
    setVisuals(new Map());
    setBoardVisuals(new Map());
    setBoardVisualAttempts(new Map());
    visualAttemptNumbersRef.current.clear();
    void (async () => {
      try {
        let t: SoulIntakeTurnView;
        try {
          t = await client.invoke('intake:turn', { gameId });
        } catch {
          try {
            t = await client.invoke('intake:recover', { gameId });
          } catch (recoverError) {
            if (!isNoRecoverableIntakeCheckpoint(recoverError)) throw recoverError;
            // No checkpoint is expected for a `skipInteractiveIntake` game,
            // but it can also contradict stale WorkspaceRunState that mounted
            // this surface. Never claim plan preparation without a terminal
            // checkpoint. Let Workspace project the contradiction into its
            // retryable needs-attention state; standalone owners keep the
            // historical no-op completion behavior.
            if (!cancelled) {
              setFinalizing(false);
              if (onUnavailableRef.current) onUnavailableRef.current();
              else onDoneRef.current?.();
            }
            return;
          }
        }
        if (cancelled) return;
        if (t.done) {
          setFinalizing(true);
          await awaitPlanReview(t.skipTarget ?? 'build');
          return;
        }
        // A non-done turn with no question is malformed: rendering it would hang
        // the surface with no footer/Continue (SoulInterviewTurnCard renders no
        // action footer when turn.question is null) and no error. Surface an
        // explicit error instead of stalling on a dead turn.
        if (!t.question) {
          setError('The interview returned an empty question.');
          setFinalizing(false);
          return;
        }
        setCurrent(t);
        setSkipTarget(t.skipTarget ?? null);
        setFinalizing(false);
        setError(null);
        // Seed turn 0's visual from the producer's `expectVisual` flag: `true`
        // (image producer wired AND this turn generates one) → `undefined` =
        // shimmer while the REVEAL image generates, resolved by the
        // intake:visual push. `false`/absent (images off, no key) → `null`
        // = text-only, NOT a shimmer that would hang forever. A structured
        // producer→consumer signal, not a renderer-side guess.
        setVisuals(new Map([[0, t.expectVisual ? undefined : null]]));
        // #2058 P3 — mount-probe recovery: re-hydrate any visual ALREADY pushed
        // before this mount (the fire-and-forget intake:visual push a
        // remount would otherwise miss → dead shimmer). Best-effort: a bridge
        // without the channel just throws, caught below; the live push still
        // covers the not-yet-generated case.
        try {
          const probed = await client.invoke('intake:visual:current', { gameId });
          if (!cancelled && Array.isArray(probed) && probed.length > 0) {
            // The probe and push channel race independently in Cloud mode. A
            // newer retry may push while this request is in flight, so merge
            // probe results through the same attempt fence as live pushes.
            const accepted = probed.filter(acceptVisualAttempt);
            const boards = accepted.filter((v) => typeof v.boardIndex === 'number');
            const singles = accepted.filter((v) => typeof v.boardIndex !== 'number');
            if (boards.length > 0) {
              setBoardVisuals((prev) => {
                const next = new Map(prev);
                for (const v of boards) next.set(v.boardIndex as number, v.image);
                return next;
              });
              setBoardVisualAttempts((prev) => {
                const next = new Map(prev);
                for (const v of boards) next.set(v.boardIndex as number, v.attempt);
                return next;
              });
            }
            if (singles.length > 0) {
              setVisuals((prev) => {
                const next = new Map(prev);
                for (const v of singles) next.set(v.questionIndex, v);
                return next;
              });
            }
          }
        } catch {
          // No mount-probe channel (older stub / not wired) — the live push path
          // still covers visuals generated after mount.
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [acceptVisualAttempt, awaitPlanReview, client, gameId]);

  // crucible#2912 round (Sean, live-testing: "nothing should be auto-picked")
  // — reset to no draft on every new question rather than seeding
  // `question.recommended`. SoulInterviewTurnCard's own render already starts
  // unselected; this state feeds the "Current pick" snapshot label below
  // (selectedOption / selectedOptionIndex), so seeding it here re-introduced
  // the same auto-pick at the state layer even after the card's own render
  // was fixed.
  useEffect(() => {
    setDraftAnswer({});
  }, [current?.questionNumber, current?.question]);

  // Tick the elapsed seconds while a turn is being generated, so the thinking row
  // counts up ("Shaping the next question… 4s") instead of sitting silent.
  useEffect(() => {
    if (!thinking) {
      setThinkingElapsedSec(0);
      return;
    }
    const started = Date.now();
    setThinkingElapsedSec(0);
    const id = setInterval(() => {
      setThinkingElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [thinking]);

  useEffect(() => {
    if (!finalizing) {
      setFinalizingElapsedSec(0);
      return;
    }
    const started = planPreparationStartedAt ?? Date.now();
    setFinalizingElapsedSec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    const id = setInterval(() => {
      setFinalizingElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [finalizing, planPreparationStartedAt]);

  // Backstop for a visual that never resolves (see VISUAL_GENERATION_TIMEOUT_MS
  // above): give up and hide the shimmer rather than show a permanently-loading
  // lie. Computes the visual index inline (mirrors the later `currentVisualIndex`
  // derivation) rather than depending on it directly — this effect runs before
  // that value is computed further down the component. Re-checks the CURRENT
  // value inside the timeout callback (not the closed-over one) so a
  // resolution that lands a moment before the deadline isn't clobbered to null.
  //
  // crucible#2919 (Bugbot, Low) — depending on `visuals` here used to reset
  // this timer on EVERY visuals-map change, not just this index's own
  // resolution (e.g. a sibling turn's own backstop firing). Deadline is now
  // anchored purely to `current` (once per turn); the setter still safely
  // no-ops if `prev.get(index)` already resolved by the time the timeout
  // fires, so a fast resolution is never clobbered.
  useEffect(() => {
    const index = current ? current.questionNumber - 1 : -1;
    if (index < 0) return;
    const id = setTimeout(() => {
      setVisuals((prev) => {
        if (prev.get(index) !== undefined) return prev;
        const next = new Map(prev);
        const now = new Date().toISOString();
        next.set(index, {
          questionIndex: index,
          image: null,
          attempt: { attemptNumber: 1, startedAt: now, finishedAt: now, failureCode: 'timeout' },
        });
        return next;
      });
    }, VISUAL_GENERATION_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [current, visualRetryEpoch]);

  // Sibling backstop for the moodboard turn's candidate board tile — same
  // silent-failure root cause as the single-visual backstop above (no FAL key
  // → the board-image generator never pushes), but a SEPARATE map
  // (boardVisuals, keyed by boardIndex, not questionIndex) needs its own
  // effect. Sean, live-testing: "moodboards didn't generate."
  //
  // crucible#2919 (Bugbot, Low) — depending on `boardVisuals` reset this
  // timer on EVERY tile arrival, so staggered/slow generation could keep
  // re-arming the 25s window indefinitely and never actually time out a
  // stuck tile. Deadline is now anchored purely to `current` (once per
  // moodboard turn); the setter still reads `prev` at fire time, so a tile
  // that already resolved is never clobbered.
  //
  // 2026-07-14 — the turn collapsed from 3 pick-one candidates to 1 steerable
  // board; this loop used to cover indices [0, 1, 2] from that era. Only
  // index 0 is ever read (`MoodboardSingleBoard`), so the other two were dead
  // writes into a map nobody consults — narrowed to just the one real index.
  useEffect(() => {
    if (current?.kind !== 'moodboard') return;
    const id = setTimeout(() => {
      const now = new Date().toISOString();
      setBoardVisualAttempts((attempts) =>
        new Map(attempts).set(0, {
          attemptNumber: 1,
          startedAt: now,
          finishedAt: now,
          failureCode: 'timeout',
        }),
      );
      setBoardVisuals((prev) => {
        if (prev.get(0) !== undefined) return prev;
        return new Map(prev).set(0, null);
      });
    }, VISUAL_GENERATION_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [current, visualRetryEpoch]);

  const submitCurrentTurn = useCallback(
    (answer: SoulInterviewAnswerInput, submit: () => Promise<unknown>) => {
      const answered = current;
      if (!answered || thinking || answerInFlightRef.current) return;
      answerInFlightRef.current = true;
      setError(null);
      const preserveQuestionUntilSaved = surface === 'desktop';
      if (preserveQuestionUntilSaved) {
        // The desktop decisions projection is checkpoint-backed, so keep the
        // draft on its question card until persistence accepts the answer.
        setSaving(true);
      } else {
        // Preserve the compact legacy surfaces' transcript behavior. They do
        // not render the checkpoint-backed Decisions panel.
        setHistory((prev) => [...prev, { turn: answered, answer }]);
        setCurrent(null);
        setDraftAnswer({});
      }
      setThinking(true);
      setFinalizing(false);
      void (async () => {
        try {
          const answerPromise = submit();
          await new Promise((resolve) => setTimeout(resolve, MIN_VISIBLE_THINKING_MS));
          const next = (await answerPromise) as SoulIntakeTurnView;
          // The component may have unmounted while the answer was in flight (the
          // create flow can navigate away) — don't setState on an unmounted tree.
          if (!mountedRef.current) return;
          // Skip owns the terminal transition once it has started. A slow
          // answer response must not repaint an obsolete next turn over it.
          if (skipInFlightRef.current) return;
          setSaving(false);
          setThinking(false);
          answerIdsRef.current.delete(answered.questionNumber);
          if (preserveQuestionUntilSaved) {
            setHistory((prev) => [...prev, { turn: answered, answer }]);
            setDraftAnswer({});
          }
          if (next.done) {
            if (preserveQuestionUntilSaved) setCurrent(null);
            setFinalizing(true);
            setError(null);
            await awaitPlanReview(answered.skipTarget ?? skipTarget);
            return;
          }
          // A non-done next turn with no question is malformed: it would render
          // a dead turn card with no Continue affordance. Surface the error
          // instead of advancing into a stalled turn.
          if (!next.question) {
            if (preserveQuestionUntilSaved) setCurrent(null);
            setFinalizing(false);
            setError('The interview returned an empty question.');
            return;
          }
          setCurrent(next);
          setSkipTarget(next.skipTarget ?? null);
          setFinalizing(false);
          setError(null);
        } catch (err) {
          if (!mountedRef.current) return;
          if (skipInFlightRef.current) return;
          setSaving(false);
          setThinking(false);
          setFinalizing(false);
          if (!preserveQuestionUntilSaved) {
            setCurrent(answered);
            setHistory((prev) => prev.slice(0, -1));
          }
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          answerInFlightRef.current = false;
        }
      })();
    },
    [awaitPlanReview, current, skipTarget, surface, thinking],
  );
  const handleAnswer = useCallback(
    (answer: SoulInterviewAnswerInput) => {
      if (!current) return;
      const answerId =
        answerIdsRef.current.get(current.questionNumber) ??
        globalThis.crypto?.randomUUID?.() ??
        `${gameId}:${current.questionNumber}:${Date.now()}`;
      answerIdsRef.current.set(current.questionNumber, answerId);
      submitCurrentTurn(answer, () =>
        client.invoke('intake:answer', {
          gameId,
          ...(answer.optionIndex !== undefined ? { optionIndex: answer.optionIndex } : {}),
          ...(answer.freeText ? { freeText: answer.freeText } : {}),
          answerId,
        }),
      );
    },
    [client, current, gameId, submitCurrentTurn],
  );
  const handleSkip = useCallback(() => {
    if (!skipTarget || finalizing || answerInFlightRef.current || skipInFlightRef.current) return;
    skipInFlightRef.current = true;
    setError(null);
    setFinalizing(true);
    setSaving(false);
    setCurrent(null);
    setThinking(false);
    void (async () => {
      try {
        await client.invoke('intake:skip', { gameId });
        await awaitPlanReview(skipTarget);
      } catch (err) {
        let recovered: SoulIntakeTurnView | null = null;
        try {
          recovered = await client.invoke('intake:turn', { gameId });
        } catch {
          // Keep the explicit skip error below. A reload can retry recovery if
          // this secondary probe also lost its transport.
        }
        if (!mountedRef.current) return;
        if (recovered?.done) {
          await awaitPlanReview(skipTarget);
          return;
        }
        setFinalizing(false);
        setThinking(false);
        if (recovered && !recovered.done && recovered.question) {
          setCurrent(recovered);
          setSkipTarget(recovered.skipTarget ?? null);
          setHistory((prev) =>
            prev.filter((entry) => entry.turn.questionNumber < recovered!.questionNumber),
          );
        }
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        skipInFlightRef.current = false;
      }
    })();
  }, [awaitPlanReview, client, finalizing, gameId, skipTarget]);
  const handleRetryPlanReview = useCallback(() => {
    if (!planReviewFailed || skipInFlightRef.current) return;
    skipInFlightRef.current = true;
    setError(null);
    setPlanReviewFailed(false);
    setFinalizing(true);
    setFinalizingElapsedSec(0);
    void (async () => {
      try {
        await client.invoke('intake:retry-plan-review', { gameId });
        await awaitPlanReview('build');
      } catch (err) {
        if (mountedRef.current) {
          setFinalizing(false);
          setPlanReviewFailed(true);
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        skipInFlightRef.current = false;
      }
    })();
  }, [awaitPlanReview, client, gameId, planReviewFailed]);
  const handleRetryVisual = useCallback(() => {
    if (!current) return;
    const questionIndex = current.questionNumber - 1;
    const boardIndex = current.kind === 'moodboard' ? 0 : undefined;
    void (async () => {
      try {
        const result = await client.invoke('intake:visual:retry', {
          gameId,
          questionIndex,
          ...(boardIndex !== undefined ? { boardIndex } : {}),
        });
        if (result.outcome === 'not-current') {
          setError('That visual belongs to an older interview question.');
          return;
        }
        if (result.outcome === 'already-running') {
          setError('That visual is already being generated.');
          return;
        }
        if (result.outcome === 'unavailable') {
          setError('Visual generation is unavailable in this workspace.');
          return;
        }
        setError(null);
        setVisualRetryEpoch((epoch) => epoch + 1);
        const attemptKey = `${questionIndex}:${boardIndex ?? 'single'}`;
        const latestAttemptNumber = visualAttemptNumbersRef.current.get(attemptKey) ?? 0;
        if (latestAttemptNumber >= result.attemptNumber) return;
        visualAttemptNumbersRef.current.set(attemptKey, result.attemptNumber);
        if (boardIndex !== undefined) {
          setBoardVisuals((prev) => new Map(prev).set(boardIndex, undefined));
          setBoardVisualAttempts((prev) => new Map(prev).set(boardIndex, undefined));
        } else {
          setVisuals((prev) => new Map(prev).set(questionIndex, undefined));
        }
      } catch (retryError) {
        setError(retryError instanceof Error ? retryError.message : String(retryError));
      }
    })();
  }, [client, current, gameId]);
  const handleCommand = useCallback(
    (command: string, source: SoulIntakeCommandSource) => {
      const recommendation = current?.question?.recommended;
      if (
        isDecideForMeCommand(command) &&
        typeof recommendation === 'number' &&
        Number.isInteger(recommendation) &&
        current?.question?.options[recommendation]
      ) {
        handleAnswer({ optionIndex: recommendation });
        return;
      }
      submitCurrentTurn({ freeText: command }, () =>
        client.invoke('intake:command', { gameId, command, source }),
      );
    },
    [client, current, gameId, handleAnswer, submitCurrentTurn],
  );
  // 2026-07-14 — "asset steering" for the moodboard turn: regenerate the sole
  // board with feedback WITHOUT answering/settling the turn (unlike
  // handleAnswer/handleCommand above, both terminal for it). The regenerated
  // image arrives via the SAME `intake:visual` push/boardVisuals map this
  // component already subscribes to (keyed by the same boardIndex), so no
  // separate visual-handling path is needed here — `steering` is purely a
  // local loading flag for the UI to show a "steering…" state while the
  // regenerate call is in flight.
  const [steering, setSteering] = useState(false);
  const handleSteer = useCallback(
    (feedback: string) => {
      if (!mountedRef.current) return;
      setSteering(true);
      void client
        .invoke('intake:regenerate-moodboard', { feedback })
        .catch(() => {
          // Best-effort, matches the session's own contract: a failed
          // regenerate leaves the prior image on screen, no error surfaced.
        })
        .finally(() => {
          if (mountedRef.current) setSteering(false);
        });
    },
    [client],
  );

  const currentVisualIndex = current ? current.questionNumber - 1 : -1;
  const currentVisual =
    current && visuals.has(currentVisualIndex) ? visuals.get(currentVisualIndex) : null;
  const lastSettled = history[history.length - 1] ?? null;
  const snapshotTurn = current ?? lastSettled?.turn ?? null;
  // crucible#2912 round — no recommended-option fallback: an unanswered
  // active question has no pick until the phone actually reports one
  // (draftAnswer.optionIndex via onDraftChange), and a settled turn shows
  // exactly what was answered, not a guess. `selectedOption` staying null
  // here is intentional — see selectedLabel's own "Pick one on your phone"
  // fallback below.
  const selectedOptionIndex = current ? draftAnswer.optionIndex : lastSettled?.answer.optionIndex;
  const selectedOption =
    selectedOptionIndex !== undefined
      ? (snapshotTurn?.question?.options[selectedOptionIndex] ?? null)
      : null;
  const phoneSurface = surface === 'phone';
  const desktopSurface = surface === 'desktop';
  const snapshot = useMemo<IntegratedSoulInterviewSnapshot>(
    () => ({
      gameId,
      current,
      historyCount: history.length,
      thinking,
      thinkingElapsedSec,
      finalizing,
      finalizingElapsedSec,
      error,
      visual: currentVisual,
      selectedOption,
      freeText: draftAnswer.freeText ?? '',
      lastAnswerFreeText: !current ? (lastSettled?.answer.freeText ?? null) : null,
      boardVisuals,
      steering,
    }),
    [
      boardVisuals,
      current,
      currentVisual,
      draftAnswer.freeText,
      error,
      finalizing,
      finalizingElapsedSec,
      gameId,
      history.length,
      lastSettled?.answer.freeText,
      lastSettled?.answer.optionIndex,
      lastSettled?.turn,
      selectedOption,
      steering,
      thinking,
      thinkingElapsedSec,
    ],
  );

  const semanticSlotStates: GuidedInterviewSemanticSlotStates = {
    prompt: current?.question || finalizing ? 'present' : 'empty',
    reveal:
      phoneSurface ||
      !current ||
      (current.kind !== 'moodboard' && (currentVisual === null || currentVisual?.image === null))
        ? 'not-applicable'
        : 'present',
    choices: !current?.question ? 'empty' : phoneSurface ? 'not-applicable' : 'present',
    freeform: !current?.question ? 'empty' : surface === 'tv' ? 'not-applicable' : 'present',
    progress: 'present',
    validation: error || planReviewFailed ? 'present' : 'empty',
    terminal:
      surface === 'tv'
        ? 'not-applicable'
        : skipTarget && !finalizing && (!desktopSurface || Boolean(current?.question))
          ? 'present'
          : current?.question && !finalizing && !phoneSurface
            ? 'present'
            : 'empty',
    dismissal:
      surface === 'tv'
        ? 'not-applicable'
        : skipTarget && !finalizing && (!desktopSurface || Boolean(current?.question))
          ? 'present'
          : 'not-applicable',
  };

  useEffect(() => {
    onSnapshot?.(snapshot);
  }, [onSnapshot, snapshot]);

  if (desktopSurface) {
    return (
      <section
        className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-[#111114]"
        data-testid="integrated-soul-interview"
        data-testid-mode="desktop"
        data-input-primitive="voice-first-text-fallback"
        data-game-id={gameId}
        data-surface="desktop"
        aria-busy={saving || thinking || finalizing}
      >
        <GuidedInterviewSemanticComposition surface="desktop" slotStates={semanticSlotStates} />
        <InterviewProgress active={finalizing ? 'review' : 'interview'} />
        {error && (
          <div
            className="mx-8 mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 lg:mx-14"
            role="alert"
          >
            {error}
          </div>
        )}
        {planReviewFailed && (
          <button
            type="button"
            onClick={handleRetryPlanReview}
            className="mx-8 mt-3 self-start rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-white/82 hover:border-white/35 lg:mx-14"
          >
            Retry preparing plan
          </button>
        )}
        {current ? (
          <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)] xl:overflow-hidden">
            <div
              className="flex min-h-[42rem] min-w-0 flex-col border-white/10 xl:min-h-0 xl:border-r"
              data-testid="guided-interview-main-pane"
            >
              {current.kind !== 'moodboard' && (
                <SoulInterviewVisualCard
                  visual={currentVisual}
                  questionIndex={currentVisualIndex}
                  onRetry={handleRetryVisual}
                />
              )}
              <SoulInterviewTurnCard
                key={`desktop-${current.questionNumber}`}
                turn={current}
                thinking={thinking}
                saving={saving}
                thinkingElapsedSec={thinkingElapsedSec}
                onAnswer={handleAnswer}
                onDraftChange={setDraftAnswer}
                onSteer={handleSteer}
                steering={steering}
                surface="desktop"
                boardVisuals={boardVisuals}
                boardVisualAttempts={boardVisualAttempts}
                onRetryVisual={handleRetryVisual}
                onFinishInterview={skipTarget ? handleSkip : undefined}
                finishInterviewTarget={skipTarget ?? undefined}
                finishInterviewDisabled={saving || thinking || finalizing}
              />
            </div>
            <InterviewDecisions turn={current} />
          </div>
        ) : !error && !planReviewFailed ? (
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-8 text-center"
            role="status"
            aria-live="polite"
            data-testid={finalizing ? 'integrated-soul-finalizing' : 'integrated-soul-loading'}
          >
            <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/12 border-t-crucible-accent" />
            <div>
              <p className="text-base font-medium text-white/82">
                {finalizing
                  ? `${planPreparationPhase ? PLAN_PREPARATION_LABELS[planPreparationPhase] : 'Preparing your build plan'}…`
                  : 'Preparing your interview…'}
              </p>
              <p className="mt-2 text-sm text-white/45">
                {finalizing
                  ? `Your answers are saved — it’s safe to leave. The editable plan will open when it is ready${finalizingElapsedSec > 0 ? ` · ${finalizingElapsedSec}s` : ''}.`
                  : 'Foundry is choosing the first useful question.'}
              </p>
            </div>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden"
      data-testid="integrated-soul-interview"
      data-input-primitive="voice-first-text-fallback"
      data-game-id={gameId}
      data-surface={surface}
    >
      <GuidedInterviewSemanticComposition surface={surface} slotStates={semanticSlotStates} />
      <div className="flex shrink-0 flex-col gap-1 px-4 pt-5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-crucible-accent">
          Interview
        </span>
        <p className="text-sm leading-snug text-white/55">
          {current
            ? `Question ${current.questionNumber}`
            : finalizing
              ? planReviewFailed
                ? 'Build plan needs attention'
                : planPreparationPhase
                  ? PLAN_PREPARATION_LABELS[planPreparationPhase]
                  : 'Preparing your build plan'
              : 'Preparing'}
        </p>
        {surface !== 'tv' && skipTarget && !finalizing && (
          <button
            type="button"
            data-testid="integrated-soul-skip"
            onClick={handleSkip}
            className="mt-2 self-start rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-white/60 transition hover:border-white/20 hover:text-white/85"
          >
            {skipTarget === 'design' ? 'Skip to Design' : 'Finish interview & review plan'}
          </button>
        )}
        {surface !== 'tv' && skipTarget === 'build' && !finalizing && (
          <p className="mt-1 text-xs text-white/45">
            You’ll review and approve the plan before Foundry starts building.
          </p>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg bg-red-500/15 p-3 text-sm text-red-300" role="alert">
          {error}
        </div>
      )}
      {planReviewFailed && (
        <button
          type="button"
          onClick={handleRetryPlanReview}
          className="mx-4 mt-3 self-start rounded-lg border border-white/15 px-3 py-2 text-sm font-medium text-white/80 hover:border-white/30 hover:text-white"
        >
          Retry preparing plan
        </button>
      )}

      {/* 2026-07-15 — Sean, live-testing: a scrollbar appeared on the phone/
          "Driver" surface. Root cause: this wrapper (unlike the component's
          own root above, which correctly clips) had no overflow rule at
          all — the default `overflow: visible` let a too-tall child spill
          into whatever ancestor DOES scroll (the panel-override mount in
          Workspace.tsx), instead of being contained here. Scrollbars are
          never allowed on TV or mobile; clip here so the turn card's own
          internal sizing/overflow-hidden is what governs, not an ancestor
          nobody intended to scroll. */}
      <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 pb-4">
        {history.length > 0 && (
          <div
            className="shrink-0 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[12px] text-white/50"
            data-testid="soul-interview-history-count"
          >
            {history.length} answered
          </div>
        )}

        {current && (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Shimmer for the live turn while the image is generating. The visuals
                map entry starts as undefined (shimmer) and flips to the visual once
                the intake:visual push arrives. Turns with no visual slot pass
                null, so follow-up questions do not reserve permanent dead space.
                The moodboard turn (P2) renders its 3 tiles INSIDE the turn card, so
                the single visual card is suppressed for it (no double-visual). */}
            {!phoneSurface && current.kind !== 'moodboard' && (
              <SoulInterviewVisualCard visual={currentVisual} questionIndex={currentVisualIndex} />
            )}
            <SoulInterviewTurnCard
              key={`live-${current.questionNumber}`}
              turn={current}
              thinking={thinking}
              saving={saving}
              thinkingElapsedSec={thinkingElapsedSec}
              onAnswer={handleAnswer}
              onCommand={handleCommand}
              onDraftChange={setDraftAnswer}
              onSteer={handleSteer}
              steering={steering}
              surface={surface}
              boardVisuals={boardVisuals}
            />
          </div>
        )}

        {/* Between turns the live turn is briefly null; keep the thinking affordance
            visible so the pause never reads as a frozen blank. */}
        {!current && thinking && (
          <div
            className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.025] px-5 py-4 text-[12px] text-white/50"
            data-testid="integrated-soul-thinking"
          >
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/15 border-t-crucible-accent" />
            <span>
              Shaping the next question
              {thinkingElapsedSec > 0 ? ` · ${thinkingElapsedSec}s` : '…'}
            </span>
          </div>
        )}

        {!current && !thinking && !error && (
          <div
            className="flex flex-col items-center gap-4 py-12 text-center"
            data-testid={finalizing ? 'integrated-soul-finalizing' : 'integrated-soul-loading'}
          >
            <div className="relative h-9 w-9">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-white/10 border-t-crucible-accent" />
              <div className="absolute inset-1.5 rounded-full bg-crucible-accent/10" />
            </div>
            <div className="grid gap-1">
              <p className="text-sm font-medium text-white/70">
                {finalizing
                  ? `Build handoff${finalizingElapsedSec > 0 ? ` · ${finalizingElapsedSec}s` : ''}`
                  : 'Dreaming up a direction…'}
              </p>
              {finalizing && (
                <p className="text-xs leading-snug text-white/40">
                  Preparing the editable game plan for review.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InterviewProgress({ active }: { active: 'interview' | 'review' }): React.JSX.Element {
  const steps = [
    { id: 'interview', label: 'Interview' },
    { id: 'review', label: 'Review plan' },
    { id: 'build', label: 'Build' },
  ] as const;
  const activeIndex = active === 'review' ? 1 : 0;
  return (
    <nav
      aria-label="Build progress"
      className="mx-auto flex w-full max-w-4xl shrink-0 items-start px-8 pb-4 pt-6 lg:px-14"
      data-testid="guided-interview-progress"
    >
      {steps.map((step, index) => {
        const reached = index <= activeIndex;
        return (
          <div key={step.id} className="flex min-w-0 flex-1 items-start last:flex-none">
            <div className="flex flex-col items-center gap-2">
              <span
                className={[
                  'grid h-7 w-7 place-items-center rounded-full border text-xs font-medium',
                  reached
                    ? 'border-crucible-accent bg-crucible-accent text-black'
                    : 'border-white/30 text-white/55',
                ].join(' ')}
                aria-current={index === activeIndex ? 'step' : undefined}
              >
                {index + 1}
              </span>
              <span
                className={`whitespace-nowrap text-xs ${reached ? 'text-crucible-accent' : 'text-white/45'}`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <span
                className={`mt-3.5 h-px min-w-8 flex-1 ${index < activeIndex ? 'bg-crucible-accent/70' : 'bg-white/20'}`}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}

function InterviewDecisions({ turn }: { turn: SoulIntakeTurnView }): React.JSX.Element {
  const decisions = turn.decisions ?? [];
  const remainingAxes = turn.remainingAxes ?? [];
  return (
    <aside
      className="min-h-0 overflow-y-auto border-t border-white/10 bg-white/[0.015] px-8 py-8 xl:border-t-0 lg:px-10"
      aria-label="Decisions so far"
      data-testid="guided-interview-decisions"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-crucible-accent">
        Decisions so far
      </p>
      {turn.direction && (
        <section className="mt-6 border-b border-white/10 pb-6">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/72">
            Locked direction
          </h2>
          <p className="mt-3 text-base leading-relaxed text-white/86">{turn.direction.thesis}</p>
          <p className="mt-2 text-sm leading-relaxed text-white/48">
            {turn.direction.referenceBar}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white/62">{turn.direction.wowBeat}</p>
        </section>
      )}
      <section className="border-b border-white/10 py-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/72">
          Accepted decisions
        </h2>
        {decisions.length > 0 ? (
          <dl className="mt-4 grid gap-5">
            {decisions.map((decision, index) => (
              <div key={`${decision.axis}-${index}`} data-testid="guided-interview-decision">
                <dt className="text-xs font-medium uppercase tracking-[0.12em] text-white/38">
                  {decision.axis}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/78">{decision.answer}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="mt-3 text-sm leading-relaxed text-white/38">
            Your saved choices will appear here.
          </p>
        )}
      </section>
      <section className="py-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/72">
          Still to decide
        </h2>
        {remainingAxes.length > 0 ? (
          <ul className="mt-4 grid gap-2 text-sm text-white/52">
            {remainingAxes.map((axis) => (
              <li key={axis} className="flex gap-2">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-crucible-accent" />
                <span>{axis}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-white/38">No required axes remain.</p>
        )}
      </section>
      <p className="border-t border-white/10 pt-5 text-xs leading-relaxed text-white/38">
        The complete build plan is created when you finish the interview.
      </p>
    </aside>
  );
}
